import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useStatusStore } from '@/stores/statusStore'
import {
  DEFAULT_SIDEBAR_MODULES,
  parseSidebarModules,
  parseUserSidebarModules,
  type SidebarModuleConfig,
  type SidebarModules,
} from '@/constants/sidebarModules'

export type { SidebarModuleConfig, SidebarModules } from '@/constants/sidebarModules'

function safeParseSidebar(raw: unknown): SidebarModules {
  return parseSidebarModules(raw)
}

/**
 * 统一的侧边栏配置：
 * - 管理员模块来自 status.SidebarModulesAdmin
 * - 用户自定义模块来自 user.sidebar_modules
 * - 其他项按角色显隐
 */
export function useSidebar() {
  const user = useAuthStore((state) => state.user)
  const status = useStatusStore((state) => state.status)

  const modules = useMemo<SidebarModules>(() => {
    const adminModules = safeParseSidebar(status?.SidebarModulesAdmin ?? DEFAULT_SIDEBAR_MODULES)
    const userModules = parseUserSidebarModules(user?.sidebarModules)

    const merged: SidebarModules = { ...adminModules }
    for (const k of Object.keys(userModules || {})) {
      const adminSection = adminModules?.[k] as SidebarModuleConfig | undefined
      const userSection = userModules[k] as SidebarModuleConfig | undefined
      if (!adminSection || adminSection.enabled === false) continue
      merged[k] = {
        ...adminSection,
        ...(userSection || {}),
      }
    }
    return merged
  }, [status, user])

  return modules
}

export function isSidebarModuleEnabled(
  modules: SidebarModules,
  section: keyof SidebarModules,
  item?: string
): boolean {
  const group = modules?.[section] as (SidebarModuleConfig & { items?: Record<string, SidebarModuleConfig> }) | undefined
  if (!group) return false
  if (group.enabled === false) return false
  if (!item) return true
  const sub = group.items?.[item]
  if (!sub) return true
  return sub.enabled !== false
}
